import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const otherFeatured = articles
    .filter((a) => a.categorySlug !== article.categorySlug && a.featured)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <article>
          <header className="relative w-full h-[320px] md:h-[450px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-black/30"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-4xl mx-auto">
                <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 mb-3">
                  {article.category}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </div>
          </header>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10">
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div
              className="article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                同分类推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        {otherFeatured.length > 0 && (
          <section className="py-10 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                精选推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {otherFeatured.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
              继续探索港片影志
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.slice(0, 5).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 text-sm rounded-full bg-background-50 border border-background-200/70 text-foreground-600 hover:text-primary-600 hover:border-primary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            datePublished: article.date,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '港片影志',
              url: 'https://pczqmcf.cn',
            },
            url: `https://pczqmcf.cn/article/${article.slug}`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://pczqmcf.cn/article/${article.slug}`,
            },
          }),
        }}
      />
    </>
  );
}